import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
  Button,
  CardContent,
  Textarea
} from '@invent/wl-ui-kit-next/ui';
import * as React from 'react';

import { NotPermitted } from '@/design';
import { ApiError, ProblemType } from '@/domains/api';
import { useCan } from '@/domains/auth';
import { formatDateTime, type TeamRoutingOutcome } from '@/domains/survey';
import type {
  IAdminParticipantDetail,
  IAdminTeamRouting
} from '../../api/participant-contracts';
import { participantsApi } from '../../api/participants-api';
import { absent } from '../../components/account-facts';
import { Fact, Section } from '../../components/admin-page';
import { refusalMessage } from '../../refusal';
import { CycleAccessPart } from './cycle-access';
import { MemberPart } from './household';
import {
  type ISaidNote,
  NOT_REREAD,
  NoteLine,
  OUT_OF_DATE,
  unanswered,
  unansweredNote
} from './said';

/**
 * The hub's Team section (SR-213's Teams block — the teams onboarding plan's R12): for each
 * member of the household that survey-api reads as a team, where it is routed and what that
 * rests on, Veritant's decision over it, and the archive.
 *
 * **The routing is survey-api's, drawn as sent** (SR-416's routing, block 2's F9). It rests on a
 * decision of Veritant's while one holds — always, until it is cleared; else on the team's own
 * answers (a registration's or its routing page's), which hold while its lead advisor is still on —
 * or off — the wirehouse list as when it answered (survey-api compares the list's answer, not the
 * firm: a move between two listed firms keeps them, a list edit can end them); else on the
 * wirehouse list — a lead at a firm on it is routed to Wires (`source: 'firm'`); else on nothing
 * (`none`). The register's `route` routes nothing any more, so the Firm fact names the firm and
 * never its route as a reason. Nothing here re-derives, re-checks or predicts it: a decision made
 * on this page is followed by a re-read, never by a routing composed on this side. The stored
 * answers are drawn as survey-api spells them (`sec_ria: no`), because no route serves the
 * questions' texts outside a question (the plan's B4) — a label table here would be a second
 * transcription of survey-api's tree.
 *
 * **Three writes, each a confirmed press, each said beside its own button** — the hub's rules:
 * a success re-reads (`reread`, which tells the activity to read again too), a refusal does not,
 * a 404 does (the page was out of date: the participant is no team to survey-api), and an answer
 * that never arrived re-reads and asks to be checked (`unanswered`). What each was told is held
 * by the page (`useTeamNotes`), keyed by the member, rather than by the control: a 404's re-read
 * can take the member out of this section, which is exactly when its sentence has to stay.
 *
 * **Veritant's decision moves the team's live responses**, in survey-api's one transaction: a draft
 * in an open cycle on another path moves to the new one, a submitted one is reopened on it (its
 * attestation cleared), a locked one stays, and `ria` moves nothing; each move is a `path_changed`
 * row on the response's Timeline. Its confirmation says so before the press. A clear follows the
 * wirehouse list or leaves the team not routed — never its earlier answers — and the responses
 * follow it too.
 *
 * "Clear Veritant's decision" is offered only while the routing rests on one (`source: 'staff'`):
 * survey-api writes nothing for a clear over any other source, and a button that reports success
 * for doing nothing is worse than none. Nothing here polls.
 *
 * All three are `responses.annotate` (SR-228): without it the decision's form and the archive's
 * press are drawn off, each under its own `NotPermitted` sentence, and the facts stay.
 */

/** An outcome in words — the header's chip, the routing's first fact and People's filter. */
export const OUTCOME_WORDS: Record<string, string> = {
  wires: 'Wires',
  independent: 'Independent',
  ria: 'RIA firm survey'
};

/** What a routing rests on, in words; `none` is a team nothing has routed — the Team section's
 *  facts and a Teams row's routing. `firm` is the wirehouse list (SR-416): the lead advisor's
 *  firm is on it, and no decision holds. */
export const SOURCE_WORDS: Record<string, string> = {
  firm: 'Its lead advisor’s firm is on the wirehouse list',
  answers: 'The team’s answers',
  staff: 'Veritant’s decision',
  none: 'Not routed yet'
};

export const OUTCOMES: TeamRoutingOutcome[] = ['wires', 'independent', 'ria'];

const DESCRIPTION =
  'Where each team is routed and what that rests on — a decision of Veritant’s, the team’s ' +
  'answers or its lead advisor’s firm on the wirehouse list, as the system derives it, ' +
  'drawn as sent.';

/** The header's routing chip for a team — "Routing unknown" while survey-api routes it nowhere;
 *  an outcome this app has no words for, as spelled. */
export function routingChip(routing: IAdminTeamRouting): string {
  return routing.outcome === null
    ? 'Routing unknown'
    : (OUTCOME_WORDS[routing.outcome] ?? routing.outcome);
}

type TeamWrite = 'routing' | 'archive';

export interface ITeamNotes {
  said: Record<string, Partial<Record<TeamWrite, ISaidNote>>>;
  say: (participantId: string, write: TeamWrite, note: Nullable<ISaidNote>) => void;
}

/** What each team's two controls were told, held by the page — see the module's JSDoc. */
export function useTeamNotes(): ITeamNotes {
  const [said, setSaid] = React.useState<ITeamNotes['said']>({});
  const say = React.useCallback<ITeamNotes['say']>((participantId, write, note) => {
    setSaid((current) => ({
      ...current,
      [participantId]: { ...current[participantId], [write]: note ?? undefined }
    }));
  }, []);
  return { said, say };
}

/** Whether survey-api reads this member as a team — a routing, or an archive stamp. */
function isTeam(member: IAdminParticipantDetail): boolean {
  return member.teamRouting !== null || member.archive !== null;
}

/** The members the section draws: every team, and any member a write here said something about,
 *  so a sentence outlives the re-read that took its team away. */
export function teamMembers(
  members: IAdminParticipantDetail[],
  notes: ITeamNotes
): IAdminParticipantDetail[] {
  return members.filter(
    (member) =>
      isTeam(member) ||
      Object.values(notes.said[member.participantId] ?? {}).some((note) => note !== undefined)
  );
}

/**
 * One write's outcome, in the hub's words — the three branches every write on this section
 * shares. `done` is the success sentence, `title` a refusal's heading, `refused` what a refusal
 * other than a 404 says.
 */
async function teamWrite(
  write: () => Promise<unknown>,
  reread: () => Promise<boolean>,
  name: string,
  copy: { done: () => string; title: string; refused: (error: unknown) => string }
): Promise<ISaidNote> {
  try {
    await write();
    const read = await reread();
    return { tone: 'done', text: copy.done() + (read ? '' : NOT_REREAD) };
  } catch (error) {
    if (unanswered(error)) return unansweredNote(await reread(), 'team');
    if (error instanceof ApiError && error.status === 404) {
      const read = await reread();
      return {
        tone: 'refused',
        title: copy.title,
        text:
          `The system does not read ${name} as a team — the page was out of date. ` +
          (read ? 'It has been read again.' : OUT_OF_DATE)
      };
    }
    return { tone: 'refused', title: copy.title, text: copy.refused(error) };
  }
}

export function TeamSection({
  members,
  household,
  notes,
  anchor,
  reread
}: {
  /** The members to draw — `teamMembers`' answer. */
  members: IAdminParticipantDetail[];
  /** The whole household, the opened participant first: whether to name each member. */
  household: IAdminParticipantDetail[];
  notes: ITeamNotes;
  anchor: string;
  reread: () => Promise<boolean>;
}) {
  const multi = household.length > 1;
  return (
    <Section
      title="Team"
      anchor={anchor}
      description={DESCRIPTION}
    >
      <CardContent className="grid gap-8">
        {members.map((member) => (
          <MemberPart
            key={member.participantId}
            member={member}
            opened={member.participantId === household[0]?.participantId}
            multi={multi}
          >
            <TeamPart
              member={member}
              headingLevel={multi ? 4 : 3}
              notes={notes.said[member.participantId] ?? {}}
              say={(write, note) => notes.say(member.participantId, write, note)}
              reread={reread}
            />
          </MemberPart>
        ))}
      </CardContent>
    </Section>
  );
}

function TeamPart({
  member,
  headingLevel,
  notes,
  say,
  reread
}: {
  member: IAdminParticipantDetail;
  headingLevel: 3 | 4;
  notes: Partial<Record<TeamWrite, ISaidNote>>;
  say: (write: TeamWrite, note: Nullable<ISaidNote>) => void;
  reread: () => Promise<boolean>;
}) {
  const Heading = headingLevel === 3 ? 'h3' : 'h4';
  const { teamRouting: routing } = member;
  // The decision's form is behind a press (SR-407): the routing above says where the team stands,
  // and three radios, a note box and a button under it on every visit outweighed it. A note the
  // last write left keeps the form open, so what it said stays beside the control it is about.
  const [deciding, setDeciding] = React.useState(false);
  const decision = React.useRef<HTMLElement>(null);
  // The press drew the form after it returned: the cursor goes to its first control once it is
  // drawn, here, not in the press — the Actions menu's "Decide routing…" presses the same button.
  React.useEffect(() => {
    if (!deciding) return;
    decision.current
      ?.querySelector<HTMLElement>(':is(button, input, [role="radio"]):not(:disabled)')
      ?.focus();
  }, [deciding]);

  if (!isTeam(member)) {
    // Drawn only for what a write here was told: the re-read after it found no team.
    return (
      <div className="grid gap-3 text-sm">
        <p className="text-muted-foreground">
          The system does not read {member.displayName} as a team on the page as read now.
        </p>
        {notes.routing && <NoteLine note={notes.routing} />}
        {notes.archive && <NoteLine note={notes.archive} />}
      </div>
    );
  }

  return (
    <div className="grid gap-6 text-sm">
      <section aria-label="Routing" className="grid gap-3">
        <Heading className="font-semibold text-[var(--color-royal-blue)]">Routing</Heading>
        {routing ? (
          <RoutingFacts routing={routing} />
        ) : (
          <p className="text-muted-foreground">
            The system reads no routing for this team — it holds no Teams response and no
            decision.
          </p>
        )}
      </section>

      {routing && (
        <section ref={decision} aria-label="Veritant’s decision" className="grid gap-3">
          <Heading className="font-semibold text-[var(--color-royal-blue)]">
            Veritant’s decision
          </Heading>
          {deciding || notes.routing ? (
            <RoutingDecision
              member={member}
              routing={routing}
              say={(note) => say('routing', note)}
              reread={reread}
            />
          ) : (
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="w-fit"
              data-open-decision
              onClick={() => setDeciding(true)}
            >
              Decide routing…
            </Button>
          )}
          {notes.routing && <NoteLine note={notes.routing} />}
        </section>
      )}

      <CycleAccessPart member={member} headingLevel={headingLevel} reread={reread} />

      <section aria-label="Archive" className="grid gap-3">
        <Heading className="font-semibold text-[var(--color-royal-blue)]">Archive</Heading>
        <ArchiveControl member={member} say={(note) => say('archive', note)} reread={reread} />
        {notes.archive && <NoteLine note={notes.archive} />}
      </section>
    </div>
  );
}

/** The routing as sent — outcome and path, what it rests on, the firm, the stored answers, and
 *  for a decision in force when it was made, by whom and the note. */
function RoutingFacts({ routing }: { routing: IAdminTeamRouting }) {
  const answers = Object.entries(routing.answers ?? {});
  return (
    <div className="grid gap-3 sm:grid-cols-2">
      <Fact
        label="Outcome"
        value={
          routing.outcome === null ? (
            absent('Not routed yet')
          ) : (
            <>
              {OUTCOME_WORDS[routing.outcome] ?? routing.outcome}
              {routing.pathCode && (
                <span className="text-muted-foreground">
                  {' '}
                  · path <code className="text-xs">{routing.pathCode}</code>
                </span>
              )}
            </>
          )
        }
      />
      <Fact label="Rests on" value={SOURCE_WORDS[routing.source] ?? routing.source} />
      <Fact
        label="Firm"
        value={
          routing.firm ? (
            <>
              {routing.firm.label} <code className="text-xs">{routing.firm.code}</code>
            </>
          ) : (
            absent('No firm on the register')
          )
        }
      />
      <Fact
        label="Answers"
        value={
          answers.length === 0 ? (
            absent('None stored')
          ) : (
            <ul aria-label="Stored answers" className="grid gap-0.5">
              {answers.map(([code, value]) => (
                <li key={code}>
                  <code className="text-xs">
                    {code}: {String(value)}
                  </code>
                </li>
              ))}
            </ul>
          )
        }
      />
      {routing.decidedAt && (
        <>
          <Fact
            label="Decided"
            value={
              <>
                {formatDateTime(routing.decidedAt)}
                <span className="block text-muted-foreground">
                  {routing.decidedBy ? `by ${routing.decidedBy.email}` : 'no account recorded'}
                </span>
              </>
            }
          />
          <Fact label="Note" value={routing.note ?? absent('No note')} />
        </>
      )}
    </div>
  );
}

/**
 * `PUT …/team-routing` — Veritant's decision: an outcome and a note, and a clear while one is in
 * force. Each a confirmed press; the confirmation says what the decision wins over.
 */
function RoutingDecision({
  member,
  routing,
  say,
  reread
}: {
  member: IAdminParticipantDetail;
  routing: IAdminTeamRouting;
  say: (note: Nullable<ISaidNote>) => void;
  reread: () => Promise<boolean>;
}) {
  const [outcome, setOutcome] = React.useState<Nullable<TeamRoutingOutcome>>(null);
  const [note, setNote] = React.useState('');
  const [confirming, setConfirming] = React.useState<Nullable<'decide' | 'clear'>>(null);
  const [busy, setBusy] = React.useState(false);
  const name = member.displayName;
  const radioName = React.useId();
  const permitted = useCan()('responses.annotate');
  const reasonId = React.useId();
  const notPermitted = permitted ? undefined : reasonId;

  const send = async (decided: Nullable<TeamRoutingOutcome>) => {
    setBusy(true);
    say(null);
    const trimmed = note.trim();
    const told = await teamWrite(
      () =>
        participantsApi.decideTeamRouting(member.participantId, {
          outcome: decided,
          ...(decided !== null && trimmed !== '' ? { note: trimmed } : {})
        }),
      reread,
      name,
      {
        done: () =>
          decided === null
            ? 'Veritant’s decision cleared — the team is routed by the wirehouse list, or not ' +
              'routed until it answers.'
            : `Routed to ${OUTCOME_WORDS[decided]} — Veritant’s decision.` +
              // survey-api's `TeamResponsePaths` moves nothing for `ria`.
              (decided === 'ria' ? '' : ' Its open responses follow.'),
        title: decided === null ? 'Not cleared' : 'Not decided',
        refused: (error) => refusalMessage(error, 'Could not record the decision')
      }
    );
    say(told);
    // Kept on a refusal, so what was typed can be corrected rather than typed again.
    if (told.tone === 'done' && decided !== null) {
      setOutcome(null);
      setNote('');
    }
    setBusy(false);
    setConfirming(null);
  };

  return (
    <div className="grid gap-3">
      {!permitted && <NotPermitted id={reasonId} what="deciding a team’s routing" />}
      <fieldset
        className="grid gap-2"
        disabled={busy || !permitted}
        aria-describedby={notPermitted}
      >
        <legend className="mb-1 text-muted-foreground">
          Veritant’s decision holds until it is cleared; the team’s answers hold while its lead is still
          on — or off — the wirehouse list, as when it answered.
        </legend>
        {OUTCOMES.map((code) => (
          <label key={code} className="flex items-center gap-2">
            <input
              type="radio"
              name={radioName}
              className="accent-[var(--color-sapphire)]"
              checked={outcome === code}
              onChange={() => setOutcome(code)}
            />
            {OUTCOME_WORDS[code]}
          </label>
        ))}
      </fieldset>
      <label className="grid gap-1">
        <span className="text-muted-foreground">Note (optional)</span>
        <Textarea
          value={note}
          disabled={busy || !permitted}
          aria-describedby={notPermitted}
          onChange={(event) => setNote(event.target.value)}
          rows={2}
        />
      </label>

      {/* Two presses open one confirmation, so the dialog is opened by the state they set rather
          than by a trigger each: a trigger's own toggle would run after the click that chose which
          press it is, and close over the choice before it. */}
      <div className="flex flex-wrap gap-2">
        <Button
          type="button"
          size="sm"
          disabled={busy || !permitted || outcome === null}
          aria-describedby={notPermitted}
          onClick={() => setConfirming('decide')}
        >
          Record Veritant’s decision
        </Button>
        {routing.source === 'staff' && (
          <Button
            type="button"
            size="sm"
            variant="outline"
            disabled={busy || !permitted}
            aria-describedby={notPermitted}
            onClick={() => setConfirming('clear')}
          >
            Clear Veritant’s decision
          </Button>
        )}
      </div>
      <AlertDialog
        open={confirming !== null}
        onOpenChange={(open) => {
          if (!open && !busy) setConfirming(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirming === 'clear'
                ? `Clear Veritant’s decision on ${name}?`
                : `Route ${name} to ${outcome ? OUTCOME_WORDS[outcome] : ''}?`}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirming === 'clear'
                ? 'The team is then routed by the wirehouse list if its lead advisor’s firm is on ' +
                  'it, else it is not routed until it answers the questions again — never by its ' +
                  'earlier answers. If the list routes it, its open responses move to Wires; if it ' +
                  'is not routed, they stay where they are.'
                : 'Veritant’s decision holds over the team’s answers and the wirehouse list until it ' +
                  'is cleared, and the team’s responses in an open cycle follow it: a draft on ' +
                  'another path moves to the new one, a submitted response is reopened on it and ' +
                  'its attestation cleared, and a locked response stays. The RIA firm survey ' +
                  'moves no response.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={busy}
              onClick={(event) => {
                // Held open until the answer is in, as the unlink's confirmation is.
                event.preventDefault();
                void send(confirming === 'clear' ? null : outcome);
              }}
            >
              {busy ? 'Saving…' : confirming === 'clear' ? 'Clear it' : 'Decide it'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

/**
 * `POST` / `DELETE …/archive` — the stamp, and the one press it offers: "Archive team" while
 * nobody has, "Restore team" once somebody did. Archiving releases the team's lead CRD, which a
 * restore adds back unverified — or is refused, 409 `team-lead-crd-taken`, when another team holds
 * it by then, which is said beside Restore.
 */
function ArchiveControl({
  member,
  say,
  reread
}: {
  member: IAdminParticipantDetail;
  say: (note: Nullable<ISaidNote>) => void;
  reread: () => Promise<boolean>;
}) {
  const [confirming, setConfirming] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const { archive, displayName: name } = member;
  const permitted = useCan()('responses.annotate');
  const reasonId = React.useId();
  const leadCrd =
    archive?.teamLeadCrd ??
    member.identifiers.find((identifier) => identifier.idType === 'TEAM_LEAD_CRD')?.value ??
    null;

  const press = async () => {
    setBusy(true);
    say(null);
    say(
      archive
        ? await teamWrite(() => participantsApi.restoreTeam(member.participantId), reread, name, {
            done: () =>
              leadCrd
                ? `Restored. The lead CRD ${leadCrd} is back, unverified.`
                : 'Restored.',
            title: 'Not restored',
            refused: (error) =>
              error instanceof ApiError && error.isProblem(ProblemType.teamLeadCrdTaken)
                ? `Another team holds ${leadCrd ?? 'this team’s lead CRD'} now, so ${name} ` +
                  'cannot be restored while it does.'
                : refusalMessage(error, 'Could not restore the team')
          })
        : await teamWrite(() => participantsApi.archiveTeam(member.participantId), reread, name, {
            done: () =>
              leadCrd ? `Archived. The lead CRD ${leadCrd} is released.` : 'Archived.',
            title: 'Not archived',
            refused: (error) => refusalMessage(error, 'Could not archive the team')
          })
    );
    setBusy(false);
    setConfirming(false);
  };

  return (
    // Not archived, the state and its press are one line (SR-407).
    <div className={archive ? 'grid gap-3' : 'flex flex-wrap items-center gap-3'}>
      {archive ? (
        <div className="grid gap-3 sm:grid-cols-2">
          <Fact
            label="Archived"
            value={
              <>
                {formatDateTime(archive.archivedAt)}
                <span className="block text-muted-foreground">by {archive.archivedBy.email}</span>
              </>
            }
          />
          <Fact
            label="Lead CRD released"
            value={
              archive.teamLeadCrd ? (
                <span className="font-mono">{archive.teamLeadCrd}</span>
              ) : (
                absent('It held none')
              )
            }
          />
        </div>
      ) : (
        <p className="text-muted-foreground">Not archived.</p>
      )}

      {!permitted && (
        <NotPermitted id={reasonId} what={archive ? 'restoring a team' : 'archiving a team'} />
      )}
      <AlertDialog open={confirming} onOpenChange={(open) => !busy && setConfirming(open)}>
        <AlertDialogTrigger asChild>
          <Button
            type="button"
            size="sm"
            variant="outline"
            disabled={busy || !permitted}
            aria-describedby={permitted ? undefined : reasonId}
            className="w-fit"
          >
            {archive ? 'Restore team' : 'Archive team'}
          </Button>
        </AlertDialogTrigger>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {archive ? `Restore ${name}?` : `Archive ${name}?`}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {archive
                ? `${name} is offered its survey again${
                    leadCrd ? `, and its lead CRD ${leadCrd} is added back, unverified` : ''
                  }.`
                : `${name} is offered no survey from its next read, and the responses it holds ` +
                  `stay as they are.${
                    leadCrd
                      ? ` Its lead CRD ${leadCrd} is released: another team can register with it, ` +
                        'and a restore is then refused while that team holds it.'
                      : ''
                  }`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={busy}
              onClick={(event) => {
                event.preventDefault();
                void press();
              }}
            >
              {busy ? 'Saving…' : archive ? 'Restore it' : 'Archive it'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
